import { useEffect, useRef, type MutableRefObject } from 'react';
import { applyDraftCaptureRequest } from './draftCapture.js';

const DRAFT_CAPTURE_FETCH_META = Symbol.for('cfa-author.draft-capture-fetch-meta');

type CaptureFetch = typeof window.fetch & {
  [DRAFT_CAPTURE_FETCH_META]?: {
    active: boolean;
    previous: typeof window.fetch;
  };
};

export function useDraftCapture({
  captureNamespace,
  draftEntityId,
  draftPatchRef,
  emptyDraftError,
  onPatchChange,
}: {
  captureNamespace: string;
  draftEntityId: number;
  draftPatchRef: MutableRefObject<Record<string, unknown> | null>;
  emptyDraftError: string;
  onPatchChange: (patch: Record<string, unknown>) => void;
}) {
  const captureQueueRef = useRef<Promise<void>>(Promise.resolve());
  const onPatchChangeRef = useRef(onPatchChange);
  onPatchChangeRef.current = onPatchChange;

  useEffect(() => {
    const previous = window.fetch;
    const meta = { active: true, previous };
    const namespace = captureNamespace.endsWith('/') ? captureNamespace.slice(0, -1) : captureNamespace;
    const captureFetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
      if (!meta.active) return meta.previous.call(window, input, init);

      const url = capturedRequestUrl(input);
      if (!url || url.origin !== window.location.origin || !url.pathname.startsWith(`${namespace}/`)) {
        return meta.previous.call(window, input, init);
      }

      try {
        const call = {
          method: capturedRequestMethod(input, init),
          path: url.pathname,
          body: await capturedRequestBody(input, init),
        };
        const queued = captureQueueRef.current.then(() => {
          try {
            const currentPatch = draftPatchRef.current;
            if (!currentPatch) return captureJsonResponse(400, { error: emptyDraftError });

            const result = applyDraftCaptureRequest(currentPatch, call, draftEntityId, namespace);
            if (!result.ok) return captureJsonResponse(result.status, { error: result.error });

            draftPatchRef.current = result.patch;
            onPatchChangeRef.current(result.patch);
            return captureJsonResponse(200, result.envelope);
          } catch (reason) {
            return captureJsonResponse(400, {
              error: reason instanceof Error ? reason.message : 'Unable to capture the player edit.',
            });
          }
        });
        captureQueueRef.current = queued.then(() => undefined, () => undefined);
        return queued;
      } catch (reason) {
        return captureJsonResponse(400, {
          error: reason instanceof Error ? reason.message : 'Unable to capture the player edit.',
        });
      }
    }) as CaptureFetch;
    captureFetch[DRAFT_CAPTURE_FETCH_META] = meta;
    window.fetch = captureFetch;

    return () => {
      meta.active = false;
      if (window.fetch === captureFetch) window.fetch = unwrapInactiveCaptureFetch(previous);
    };
  }, [captureNamespace, draftEntityId, draftPatchRef, emptyDraftError]);
}

function capturedRequestUrl(input: RequestInfo | URL): URL | null {
  try {
    const rawUrl = typeof input === 'string' || input instanceof URL ? String(input) : input.url;
    return new URL(rawUrl, window.location.href);
  } catch {
    return null;
  }
}

function capturedRequestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method;
  return typeof input === 'object' && 'method' in input ? String(input.method) : 'GET';
}

async function capturedRequestBody(input: RequestInfo | URL, init?: RequestInit): Promise<unknown> {
  if (init?.body != null) {
    if (typeof init.body === 'string') return JSON.parse(init.body);
    return new Response(init.body).json();
  }
  if (typeof input === 'object' && 'clone' in input && typeof input.clone === 'function') {
    return input.clone().json();
  }
  return undefined;
}

function captureJsonResponse(status: number, payload: Record<string, unknown>): Response {
  return new Response(JSON.stringify(payload), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

function unwrapInactiveCaptureFetch(fetchImpl: typeof window.fetch): typeof window.fetch {
  let current = fetchImpl as CaptureFetch;
  const visited = new Set<CaptureFetch>();
  while (!visited.has(current)) {
    const meta = current[DRAFT_CAPTURE_FETCH_META];
    if (!meta || meta.active) break;
    visited.add(current);
    current = meta.previous;
  }
  return current;
}
